import type { Variant } from '../../gameTypes.js';
import {
  gapAfterAnchor,
  JumpPattern,
  remainingAnchors,
  tierAtLeast,
  type JumpPatternContext,
  type PatternPlacement,
} from './jumpPatternBase.js';
import { CORRIDOR_CEIL_ROWS, PLATFORM_MAX_CELLS, PLATFORM_MIN_CELLS } from './jumpPatternStamp.js';

/** Minimum standable plateau width (columns) — enough to land on and ride. */
const MIN_RIDE_COLS = 5;
/** Default plateau width (columns) for a tap-derived (non-sustained) mount. */
const DEFAULT_RIDE_COLS = 6;
/** Longest plateau a single ride may span (columns) — truncate dead spinners. */
const MAX_RIDE_COLS = 18;
/** A platform/staircase needs at least this much runway to the next onset (beats). */
const MIN_TERRAIN_ROOM_BEATS = 1.0;

/** Ascending-staircase step count by tier (each step climbs one cell). */
const MAX_STAIR_STEPS_FAST = 4;
const MAX_STAIR_STEPS_BASE = 3;
/** Flat top to stand on past the last step before the descent (columns). */
const STAIR_TOP_RIDE_COLS = 5;
/** Room (beats) past the staircase required before adding the "no-jump" ceiling. */
const STAIR_CEILING_MIN_ROOM_BEATS = 2.0;
/** Gap (columns) from the descent to the start of the "no-jump" ceiling. */
const STAIR_CEILING_LEAD_COLS = 4;
const STAIR_CEILING_WIDTH_COLS = 4;
/** Chance the staircase adds its "drop off, don't jump" ceiling cap (Hard+). */
const STAIR_CEILING_TWIST = 0.5;

/**
 * P5 — Step-Up & Ride. The floor rises into a plateau the cube mounts, rides,
 * then walks off the far side (a safe step-down) back to baseline. Sustained
 * onsets (sliders/spinners) ride for their full length; taps get a default
 * plateau. Teaches mount timing and the restraint to ride without panic-tapping.
 */
export class StepUpRidePattern extends JumpPattern {
  readonly id = 'step-up-ride';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 1;
  readonly baseWeight = 0.5;

  eligible(ctx: JumpPatternContext): boolean {
    return gapAfterAnchor(ctx, 0) >= MIN_TERRAIN_ROOM_BEATS;
  }

  weight(ctx: JumpPatternContext): number {
    // Sustained notes WANT to be ridden — bias hard toward terrain there.
    return this.baseWeight * (ctx.anchors[ctx.index].kind === 'circle' ? 1 : 2.4);
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const anchor = ctx.anchors[ctx.index];
    const room = gapAfterAnchor(ctx, 0);
    // Room budget in columns, leaving a beat of clear landing before the next onset.
    const roomCols = Math.floor((room - 0.5) / ctx.cellBeats);
    const sustainCols =
      anchor.kind === 'circle'
        ? DEFAULT_RIDE_COLS
        : Math.round((anchor.endBeat - anchor.beat) / ctx.cellBeats);
    const spanCols = Math.max(MIN_RIDE_COLS, Math.min(MAX_RIDE_COLS, sustainCols, roomCols));
    const heightCells =
      PLATFORM_MIN_CELLS + Math.floor(ctx.rng.place() * (PLATFORM_MAX_CELLS - PLATFORM_MIN_CELLS + 1));
    const placed = ctx.stamper.platform(anchor.beat, heightCells, spanCols);
    if (!placed) {
      // Couldn't fit the plateau — fall back to a single full-jump obstacle so
      // the onset still carries something.
      ctx.stamper.tallSpike(anchor.beat);
    }
    return { consumed: 1 };
  }
}

/**
 * P6 — Ascending Staircase. A run of rising one-cell steps, each mounted on an
 * onset (hold to auto-hop up them in rhythm), topped by a short plateau, then a
 * step-down back to baseline you drop off.
 *
 * Twist — "drop off, don't jump" (Hard+): a ceiling slab sits over the baseline
 * just past the descent. Walk off the top and stay grounded to slip under it; a
 * held auto-hop off the top step arcs up and smacks it. The safe line (release →
 * walk off → grounded) is what the planner clears, so it stays fair.
 */
export class AscendingStaircasePattern extends JumpPattern {
  readonly id = 'ascending-staircase';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 3;
  readonly baseWeight = 0.4;

  eligible(ctx: JumpPatternContext): boolean {
    // Every step needs clear runway to land on; check the steps we might use.
    const maxSteps = tierAtLeast(ctx.tier, 'hard') ? MAX_STAIR_STEPS_FAST : MAX_STAIR_STEPS_BASE;
    const steps = Math.min(maxSteps, remainingAnchors(ctx));
    if (steps < this.minAnchors) {
      return false;
    }
    for (let i = 0; i < steps - 1; i += 1) {
      if (gapAfterAnchor(ctx, i) < MIN_TERRAIN_ROOM_BEATS) {
        return false;
      }
    }
    return true;
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const maxSteps = tierAtLeast(ctx.tier, 'hard') ? MAX_STAIR_STEPS_FAST : MAX_STAIR_STEPS_BASE;
    const steps = Math.min(maxSteps, remainingAnchors(ctx));
    const startCols: number[] = [];
    for (let i = 0; i < steps; i += 1) {
      startCols.push(ctx.stamper.fullPressCol(ctx.anchors[ctx.index + i].beat));
    }
    const stairEnd = startCols[steps - 1] + STAIR_TOP_RIDE_COLS;

    // Paint low→high so each higher run overwrites the previous one's tail,
    // leaving a clean ascending staircase; truncate if a step can't fit.
    let painted = 0;
    for (let i = 0; i < steps; i += 1) {
      if (!ctx.stamper.raiseFloor(startCols[i], stairEnd, i + 1)) {
        break;
      }
      painted = i + 1;
    }
    if (painted === 0) {
      // Degenerate (near a bound) — keep the onset meaningful with a spike.
      ctx.stamper.tallSpike(ctx.anchors[ctx.index].beat);
      return { consumed: 1 };
    }

    if (tierAtLeast(ctx.tier, 'hard') && ctx.rng.twist() < STAIR_CEILING_TWIST) {
      this.maybeCapWithCeiling(ctx, painted, stairEnd);
    }
    return { consumed: painted };
  }

  /** Place the "don't jump off the top" ceiling over baseline past the descent. */
  private maybeCapWithCeiling(ctx: JumpPatternContext, painted: number, stairEnd: number): void {
    if (gapAfterAnchor(ctx, painted - 1) < STAIR_CEILING_MIN_ROOM_BEATS) {
      return;
    }
    const ceilStart = stairEnd + STAIR_CEILING_LEAD_COLS;
    const ceilEnd = ceilStart + STAIR_CEILING_WIDTH_COLS;
    if (ctx.stamper.canPlaceSpan(ceilStart, ceilEnd) && ctx.stamper.isClearStrip(ceilStart, ceilEnd)) {
      ctx.stamper.placeCeiling(ceilStart, ceilEnd, CORRIDOR_CEIL_ROWS);
    }
  }
}
